import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { normalizeString } from '../lib/textUtils';
import type { PlaceDraft } from '../lib/placeImport';
import type { Bbox } from '../lib/placeHighlight';

export type TeamRole = "Global" | "Parking Only" | "Cashless Only" | "Check-in Only" | string;

export interface Category {
  id: string;
  name: string;
  managerEmails?: string[]; // legacy, kept for backward compat
  managerToken?: string; // UUID token for link-based access
  isGeneral?: boolean;
  isArtistCategory?: boolean;
  isPublic?: boolean;
  orgId: string;
  projectId?: string;
  managerEmail?: string;
  managerEmailConfirmed?: boolean;
  managerVerificationCode?: string;
  managerVerificationCodeSentAt?: string;
}

export interface Project {
  id: string;
  orgId: string;
  name: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
}

export interface Organization {
  id: string;
  name: string;
  adminToken: string;
  logoUrl?: string;
  allowAdminSettings: boolean;
  adminEmail?: string;
  settings: {
    minRestTimeHours: number;
    appLogoUrl?: string;
    currency?: string;
    timezone?: string;
    embedConfig?: {
      enabled?: boolean;
      visiblePoints?: string[];
      pointDescriptions?: Record<string, string>;
      lineUpTitle?: string;
      showLineUp?: boolean;
      primaryColor?: string;       // hex color for tabs, accents, time text
      logoUrl?: string;            // custom logo (overrides org logo)
      title?: string;              // custom title (overrides org name)
      customCss?: string;          // raw CSS injected into embed (superadmin only)
      headerBg?: string;           // header/menu background color
      pointColor?: string;         // normal point icon color on map
      stageColor?: string;         // stage point icon color on map
      lineupBg?: string;           // timeline list background
      lineupCardBg?: string;       // timeline card background
      lineupTextColor?: string;    // timeline text color (artist names)
      secondaryTitleColor?: string; // date headers + stage names
      lineupTitleColor?: string;   // main Line Up title color
    };
  };
  createdAt: string;
}

export interface CurrentUser {
  role: 'admin' | 'manager' | 'staff' | 'superadmin';
  categoryId?: string; // If manager, they can only access this category
  teamId?: string; // If team
  email?: string;
}

export interface TeamMessage {
  id: string;
  teamId: string;
  orgId?: string;
  projectId?: string;
  content: string;
  timestamp: string;
  read: boolean;
  fromTeam?: boolean; // true = sent by team (reply), false/undefined = sent by manager/admin
  senderName?: string; // Nom de l'expéditeur : "Admin", "Manager [cat]", ou nom du team
  recipientTeamId?: string; // ID du team member destinataire (admin/manager) — pour filtrer les notifs
  imageUrl?: string | null; // Base64 data URI for attached image
  voiceUrl?: string | null; // Base64 data URI for voice recording
}

export interface MessageApprovalRequest {
  id: string;
  teamId: string;
  teamName: string;
  categoryId?: string;
  orgId?: string;
  projectId?: string;
  content: string;
  imageUrl?: string | null;
  voiceUrl?: string | null;
  recipientIds: string[];
  recipientNames: string[];
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  expiresAt: string;
}

export interface CustomButton {
  label: string;
  url: string;
  color: string; // tailwind color key: 'indigo'|'emerald'|'amber'|'rose'|'sky'|'purple'
  icon?: string; // lucide icon name
}

export interface Team {
  id: string;
  categoryId?: string;
  orgId?: string | null;
  name: string;
  role?: string; // deprecated, use roles
  roles?: string[];
  email: string;
  phone: string;
  color?: string;
  isOnDuty?: boolean;
  accessPassword?: string; // legacy
  teamToken?: string;     // UUID token for link-based access (like managerToken)
  unavailableDates?: string[]; // YYYY-MM-DD
  photo?: string; // Base64 compressed image
  customButtons?: CustomButton[]; // Up to 3 custom action buttons
  status?: string;
  excludeFromMagicFill?: boolean;
  specialties?: string[]; // Array of Point IDs
  isDistributor?: boolean;
  distAllowScan?: boolean;
  distAllowDirect?: boolean;
  distAllowCheckin?: boolean;
  distAllowLinkWallet?: boolean;  // Link QR wallet to team members
  distAllowView?: boolean;  // View team list + quotas without distribution rights
  isArtist?: boolean;
  // Shared team public profile fields
  isProfileVisible?: boolean;
  emailConfirmed?: boolean;
  verificationCode?: string | null;
  verificationCodeSentAt?: string | null;
  publicDescription?: string;
  publicSkills?: string[];
  indicativeDailyRate?: number;
  geographicalZone?: string;
  proposedCurrency?: string;
  pwaInstalled?: boolean;
  fcmToken?: string;
  linkedWalletQr?: string | null;  // External QR code linked to this team for wallet/check-in
  memberCount?: number;       // Number of members in artist group (default 1 = solo)
  parentArtistId?: string;    // If set, this is a sub-member of an artist group
  memberIndex?: number;       // 1-based index within the artist group
  categoryIds?: string[];     // Multiple categories the member belongs to (for cross-category visibility)
  isCategorySupervisor?: boolean; // If true, sees all shifts in their categories (not just their own)
  assignedTeamId?: string;       // For artists: the team member assigned as their contact
  disableMessageEmails?: boolean; // If true, don't send email notifications for messages
}

export type CustomFieldType = 'textbox' | 'number' | 'date' | 'radio' | 'checkbox';

export interface CustomFieldDefinition {
  id: string;
  orgId: string;
  label: string;
  fieldType: CustomFieldType;
  options: string[];
  required: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface TeamCustomFieldValue {
  id: string;
  fieldId: string;
  teamId: string;
  value: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SharedTeamOrganization {
  id: string;
  teamId: string;
  orgId: string;
  createdAt: string;
}

export interface TimeSlot {
  start: string; // ISO 8601 string, e.g. "2024-07-10T08:00"
  end: string;
  requiredTeam?: number; // default 1
  isHard?: boolean; // If true, the slot counts for penury score
}

export interface OperationalPoint {
  id: string;
  categoryId?: string;
  orgId?: string;
  projectId?: string;
  name: string;
  requiredTeam: number; // default 1
  openingHours: TimeSlot[]; // Can be multiple per day
  location?: { lat: number; lng: number }; // For Google Maps
  iconName?: string;
  descriptionMarkdown?: string;
  swapEnabled?: boolean; // Allow team to request shift swaps at this point
  unavailabilityEnabled?: boolean; // Allow team at this point to declare unavailabilities
  isMealPoint?: boolean; // If true, this point is considered a meal point (Restaurant, etc.)
  isMapFocus?: boolean; // If true, this point will be highlighted as a focus point on the live map
  isRestricted?: boolean; // If true, only team with this point in their specialties can work here without alert
  isStage?: boolean; // If true, only artists can be assigned to this point
}

export interface Task {
  id: string;
  name: string;
  categoryId?: string;
  orgId?: string;
  projectId?: string;
  requiredTeam: number;
  openingHours: TimeSlot[];
  iconName?: string;
  descriptionMarkdown?: string;
}

export interface Shift {
  id: string;
  pointId: string;
  taskId?: string; // For task shifts (alternative to pointId)
  teamId: string;
  orgId?: string;
  projectId?: string;
  start: string; // ISO 8601 string
  end: string; // ISO 8601 string
  description: string;
  actualStart?: string; // When they actually checked in
  actualEnd?: string; // When they actually checked out
  swapRequested?: boolean; // When team requests a replacement
  shiftType?: 'standard' | 'soundcheck' | 'performance';
  proposedAmount?: number;
  agreedAmount?: number;
  amountNegotiatedByTeam?: boolean;
  proposedCurrency?: string; // TND, EUR, USD
  agreementStatus?: string; // approved, pending_agreement, agreed, rejected, negotiated
}

export interface Issue {
  id: string;
  pointId: string;
  teamId: string;
  orgId?: string;
  projectId?: string;
  teamName: string;
  message: string;
  timestamp: string;
  resolved: boolean;
  type: 'incident_public' | 'incident_internal' | 'alert' | string;
}

export interface OfferProduct {
  id: string;
  name: string;
  icon: string;
  defaultQuota: number;
  pointsValue: number;
  orgId: string;
  projectId?: string;
  categoryQuotas?: { categoryId: string; quota: number }[]; // embedded from offer_product_category_quotas during sync
}

export interface OfferProductCategoryQuota {
  id: string;
  productId: string;
  categoryId: string;
  quota: number;
  orgId: string;
  projectId?: string | null;
}

export interface TeamCustomQuota {
  id: string;
  teamId: string;
  productName: string;
  customQuota: number;
  orgId: string;
  projectId?: string;
}

export interface TeamConsumption {
  id: string;
  teamId: string;
  productName: string;
  quantity: number;
  pointsValue: number;
  scannedBy: string; // teamId of distributor
  pointId?: string; // pointId of location
  createdAt: string;
  orgId: string;
  projectId?: string;
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export interface TeamCheckin {
  id: string;
  teamId: string;
  pointId: string;
  scannedBy: string;
  createdAt: string;
  orgId: string;
  projectId?: string;
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export interface AppState {
  categories: Category[];
  currentUser: CurrentUser;
  team: Team[];
  points: OperationalPoint[];
  tasks: Task[];
  shifts: Shift[];
  issues: Issue[];
  messages: TeamMessage[];
  approvalRequests: MessageApprovalRequest[];
  orgAdminEmails: { email: string; orgId: string }[];
  categoryManagerEmails: { email: string; categoryId: string; orgId: string }[];
  mapFocus: {
    lat: number;
    lng: number;
    zoomLevel: number;
    /** Nom du lieu recherché — renseigné uniquement par la recherche. */
    placeName?: string;
    /** Contour du lieu recherché, s'il en a un ; sinon un cercle est dessiné. */
    bbox?: Bbox;
  } | null;
  
  organizations: Organization[];
  currentOrgId: string | null;
  projects: Project[];
  currentProjectId: string | null;
  impersonatedTeamId: string | null;
  viewAsManagerCategoryId: string | null;

  offerProducts: OfferProduct[];
  offerProductCategoryQuotas: OfferProductCategoryQuota[];
  customQuotas: TeamCustomQuota[];
  consumptions: TeamConsumption[];
  checkins: TeamCheckin[];
  sharedTeamOrganizations: SharedTeamOrganization[];

  customFieldDefinitions: CustomFieldDefinition[];
  teamCustomFieldValues: TeamCustomFieldValue[];

  globalSettings: {
    minRestTimeHours: number;
    appLogoUrl?: string;
    mapProvider?: 'mapbox' | 'google';
  };

  // Actions
  addCategory: (category: Omit<Category, 'id' | 'orgId'>) => void;
  updateCategory: (id: string, category: Partial<Category>) => void;
  deleteCategory: (id: string) => void;
  setCurrentUser: (user: CurrentUser) => void;

  setMapFocus: (focus: AppState['mapFocus']) => void;
  updateGlobalSettings: (settings: Partial<AppState['globalSettings']>) => void;

  addTeam: (team: Omit<Team, 'id' | 'orgId'>) => string | null;
  updateTeam: (id: string, team: Partial<Team>) => void;
  deleteTeam: (id: string) => void;

  addPoint: (point: Omit<OperationalPoint, 'id'> & { id?: string }) => void;
  updatePoint: (id: string, point: Partial<OperationalPoint>) => void;
  deletePoint: (id: string) => void;

  addTask: (task: Omit<Task, 'id' | 'orgId'>) => void;
  updateTask: (id: string, task: Partial<Task>) => void;
  deleteTask: (id: string) => void;

  addShift: (shift: Omit<Shift, 'id'>) => void;
  updateShift: (id: string, shift: Partial<Shift>) => void;
  deleteShift: (id: string) => void;

  addIssue: (issue: Omit<Issue, 'id' | 'timestamp' | 'resolved'>) => void;
  resolveIssue: (id: string) => void;

  addMessage: (teamId: string, content: string, senderName?: string, imageUrl?: string | null, voiceUrl?: string | null) => void;
  addReply: (teamId: string, content: string, senderName?: string, imageUrl?: string | null, voiceUrl?: string | null) => void;
  addBroadcastMessage: (teamIds: string[], content: string, senderLabel: string) => void;
  markMessageRead: (id: string) => void;
  markAllMessagesRead: () => void;
  markTeamMessagesRead: (teamId: string) => void;

  // Approval Request Actions
  setApprovalRequests: (requests: MessageApprovalRequest[]) => void;
  createApprovalRequest: (request: Omit<MessageApprovalRequest, 'id' | 'status' | 'createdAt' | 'expiresAt'>) => void;
  approveRequest: (id: string, approvedBy: string) => void;
  rejectRequest: (id: string) => void;
  relanceRequest: (id: string) => void;
  cancelRequest: (id: string) => void;
  addTeamDirectMessages: (teamIds: string[], content: string, senderName: string, senderTeamId: string, imageUrl?: string | null, voiceUrl?: string | null) => void;
  
  // Org & Project Actions
  addOrganization: (name: string) => void;
  setCurrentOrgId: (id: string | null) => void;
  addProject: (name: string, startDate?: string, endDate?: string) => Promise<string>;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  setCurrentProjectId: (id: string | null) => void;
  duplicateProjectConfig: (sourceProjectId: string, targetProjectId: string) => Promise<void>;
  /** Importe des lieux depuis un fichier de programmation (voir `src/lib/placeImport.ts`). */
  importPlaces: (
    places: PlaceDraft[],
    options: { categoryId?: string }
  ) => Promise<{ created: number; updated: number; error: string | null }>;
  setImpersonatedTeamId: (id: string | null) => void;
  setViewAsManagerCategoryId: (id: string | null) => void;

  // Team Offers Actions
  addOfferProduct: (product: Omit<OfferProduct, 'id' | 'orgId'>, categoryQuotas?: { categoryId: string; quota: number }[]) => Promise<void>;
  setProductCategoryQuota: (productId: string, categoryId: string, quota: number) => void;
  deleteProductCategoryQuota: (productId: string, categoryId: string) => void;
  deleteOfferProduct: (id: string) => void;
  updateOfferProduct: (id: string, product: Partial<OfferProduct>) => void;
  updateTeamDistributor: (teamId: string, rights: Partial<Team>) => void;
  setCustomQuota: (teamId: string, productName: string, quota: number) => void;
  addTeamConsumption: (consumption: Omit<TeamConsumption, 'id' | 'orgId' | 'createdAt'>) => void;
  deleteTeamConsumption: (id: string, deletedBy: string) => void;
  addTeamCheckin: (checkin: Omit<TeamCheckin, 'id' | 'orgId' | 'createdAt'>) => void;
  deleteTeamCheckin: (id: string, deletedBy: string) => void;
  resetAllQuotas: (orgId: string) => void;
  scheduleQuotaReset: (orgId: string, dateTime: string | null) => void;

  // UI State for Messaging
  messagingDrawer: { isOpen: boolean; selectedTeamId: string };
  openReply: (teamId: string) => void;
  setMessagingDrawerOpen: (isOpen: boolean) => void;
  addSharedTeamLink: (teamId: string, orgId: string) => Promise<string | null>;
  removeSharedTeamLink: (teamId: string, orgId: string) => Promise<void>;

  // Custom Field Actions
  addCustomFieldDefinition: (field: Omit<CustomFieldDefinition, 'id' | 'orgId' | 'createdAt'>) => void;
  updateCustomFieldDefinition: (id: string, updates: Partial<CustomFieldDefinition>) => void;
  deleteCustomFieldDefinition: (id: string) => void;
  reorderCustomFieldDefinitions: (orderedIds: string[]) => void;
  setTeamCustomFieldValue: (teamId: string, fieldId: string, value: string) => void;
  setTeamCustomFieldValues: (teamId: string, values: { fieldId: string; value: string }[]) => void;
}

export const getActiveDates = (points: OperationalPoint[]): string[] => {
  const dates = new Set<string>();
  points.forEach(point => {
    point.openingHours.forEach(slot => {
      if (slot.start) dates.add(slot.start.split('T')[0]);
      if (slot.end) dates.add(slot.end.split('T')[0]);
    });
  });
  
  const sortedDates = Array.from(dates).sort();
  if (sortedDates.length === 0) {
    const today = new Date();
    return [today.toISOString().split('T')[0]];
  }
  return sortedDates;
};

export const useStore = create<AppState>()(
  (set) => ({
      categories: [],
      currentUser: { role: 'admin' },
      team: [],
      points: [],
      tasks: [],
      shifts: [],
      issues: [],
      messages: [],
      approvalRequests: [],
      orgAdminEmails: [],
      categoryManagerEmails: [],
      mapFocus: null,
      organizations: [],
      currentOrgId: typeof window !== 'undefined' ? localStorage.getItem('wfm_current_org_id') : null,
      projects: [],
      currentProjectId: null,
      impersonatedTeamId: null,
      viewAsManagerCategoryId: null,
      offerProducts: [],
      offerProductCategoryQuotas: [],
      customQuotas: [],
      consumptions: [],
      checkins: [],
      sharedTeamOrganizations: [],
      customFieldDefinitions: [],
      teamCustomFieldValues: [],
      globalSettings: { minRestTimeHours: 8, mapProvider: 'mapbox' },
      messagingDrawer: { isOpen: false, selectedTeamId: '' },

      addCategory: (c) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newCat = { ...c, id, orgId, projectId: projectId || undefined };
        set((state) => ({ categories: [...state.categories, newCat] }));
        supabase.from('categories').insert({ id, name: newCat.name, is_general: newCat.isGeneral, is_artist_category: newCat.isArtistCategory || false, is_public: newCat.isPublic || false, manager_token: newCat.managerToken, manager_emails: newCat.managerEmails, org_id: orgId, project_id: projectId }).then();
      },
      updateCategory: (id, updates) => {
        set((state) => ({ categories: state.categories.map(c => c.id === id ? { ...c, ...updates } : c) }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.isGeneral !== undefined) dbUpdates.is_general = updates.isGeneral;
        if (updates.isArtistCategory !== undefined) dbUpdates.is_artist_category = updates.isArtistCategory;
        if (updates.isPublic !== undefined) dbUpdates.is_public = updates.isPublic;
        if (updates.managerToken !== undefined) dbUpdates.manager_token = updates.managerToken;
        if (updates.managerEmails !== undefined) dbUpdates.manager_emails = updates.managerEmails;
        supabase.from('categories').update(dbUpdates).eq('id', id).then();
      },
      deleteCategory: (id) => {
        set((state) => ({
          categories: state.categories.filter(c => c.id !== id),
          points: state.points.filter(p => p.categoryId !== id),
          team: state.team.filter(s => s.categoryId !== id),
        }));
        // Soft delete
        supabase.from('categories').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },
      setCurrentUser: (user) => set({ currentUser: user }),

      setMapFocus: (focus) => set({ mapFocus: focus }),
      updateGlobalSettings: (settings) => {
        const state = useStore.getState();
        const newGlobalSettings = { ...state.globalSettings, ...settings };
        set({ globalSettings: newGlobalSettings });
        
        // 1. Update Global Settings Table (Legacy/Logo)
        const dbUpdates: any = { id: 1 };
        if (settings.minRestTimeHours !== undefined) dbUpdates.min_rest_time_hours = settings.minRestTimeHours;
        if (Object.prototype.hasOwnProperty.call(settings, 'appLogoUrl')) dbUpdates.app_logo_url = settings.appLogoUrl ?? null;
        if (settings.mapProvider !== undefined) dbUpdates.map_provider = settings.mapProvider;
        
        supabase.from('global_settings').upsert(dbUpdates, { onConflict: 'id' }).then(({ error }) => {
          if (error) console.error('[GlobalSettings] Erreur sauvegarde:', error.message);
        });

        // 2. Update Active Organization Settings (JSON)
        if (state.currentOrgId) {
          const org = state.organizations.find(o => o.id === state.currentOrgId);
          if (org) {
            const currentSettings = org.settings || {};
            const updatedSettings = {
              ...currentSettings,
              min_rest_time_hours: newGlobalSettings.minRestTimeHours,
              app_logo_url: newGlobalSettings.appLogoUrl,
              map_provider: newGlobalSettings.mapProvider
            };
            supabase.from('organizations')
              .update({ settings: updatedSettings })
              .eq('id', state.currentOrgId)
              .then();
          }
        }
      },

      addTeam: (s) => {
        const orgId = useStore.getState().currentOrgId;
        if (!orgId) return null;

        // ── Normalization helper for name dedup ──
        const normalizeName = (str: string) =>
          (str || '').toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]/g, '')
            .trim();

        // ── Helper: merge roles arrays (union, deduplicated) ──
        const mergeRoles = (existingRoles: string[] | undefined, newRole: string | undefined, newRoles: string[] | undefined): string[] => {
          const merged = new Set<string>();
          if (existingRoles) existingRoles.forEach(r => merged.add(r));
          if (newRoles) newRoles.forEach(r => { if (r) merged.add(r); });
          // Also add the single role if it's not empty and not already in the array
          if (newRole && newRole.trim()) merged.add(newRole.trim());
          return Array.from(merged);
        };

        // ── Helper: perform the merge update on an existing team member ──
        const mergeAndUpdate = (existing: Team) => {
          const mergedRoles = mergeRoles(existing.roles, s.role, s.roles);
          const merged = {
            name: s.name || existing.name,
            email: existing.email || s.email,
            phone: s.phone || existing.phone,
            categoryId: existing.categoryId || s.categoryId,
            role: existing.role || s.role,
            roles: mergedRoles,
            color: existing.color || s.color,
            photo: existing.photo || s.photo,
          };
          set((state) => ({
            team: state.team.map(st => st.id === existing.id ? { ...st, ...merged } : st)
          }));
          const dbUpdates: any = {};
          if (merged.name !== undefined) dbUpdates.name = merged.name;
          if (merged.email !== undefined) dbUpdates.email = merged.email;
          if (merged.phone !== undefined) dbUpdates.phone = merged.phone;
          if (merged.categoryId !== undefined) dbUpdates.category_id = merged.categoryId || null;
          if (merged.role !== undefined) dbUpdates.role = merged.role;
          if (merged.roles !== undefined) dbUpdates.roles = merged.roles;
          if (merged.color !== undefined) dbUpdates.color = merged.color;
          if (merged.photo !== undefined) dbUpdates.photo_url = merged.photo;
          supabase.from('staff').update(dbUpdates).eq('id', existing.id).then(({ error }) => {
            if (error) console.error('[addTeam dedup] Update error:', error.message);
          });
          return existing.id;
        };

        // ── Dedup #1: same email ──
        if (s.email && s.email.includes('@')) {
          const cleanEmail = s.email.toLowerCase().trim();
          const existingByEmail = useStore.getState().team.find(
            st => st.email?.toLowerCase().trim() === cleanEmail &&
                  (!st.orgId || st.orgId === orgId)
          );
          if (existingByEmail) return mergeAndUpdate(existingByEmail);
        }

        // ── Dedup #2: same normalized name (cross-category, even without email) ──
        if (s.name && s.name.trim()) {
          const normName = normalizeName(s.name);
          if (normName) {
            const existingByName = useStore.getState().team.find(
              st => normalizeName(st.name) === normName
            );
            if (existingByName) return mergeAndUpdate(existingByName);
          }
        }

        const id = uuidv4();
        const memberCount = s.memberCount ?? 1;
        const newTeam = { ...s, id, orgId, memberCount };
        set((state) => ({ team: [...state.team, newTeam] }));
        supabase.from('staff').insert({
          id, name: newTeam.name, role: newTeam.role, roles: newTeam.roles, color: newTeam.color,
          email: newTeam.email, phone: newTeam.phone, category_id: newTeam.categoryId || null,
          staff_token: newTeam.teamToken, access_password: newTeam.accessPassword, is_on_duty: newTeam.isOnDuty,
          photo_url: newTeam.photo, org_id: orgId,
          is_distributor: newTeam.isDistributor || false,
          dist_allow_scan: newTeam.distAllowScan || false,
          dist_allow_direct: newTeam.distAllowDirect || false,
          dist_allow_checkin: newTeam.distAllowCheckin || false,
          dist_allow_link_wallet: newTeam.distAllowLinkWallet || false,
          dist_allow_view: newTeam.distAllowView || false,
          is_artist: newTeam.isArtist || false,
          is_profile_visible: newTeam.isProfileVisible || false,
          email_confirmed: newTeam.emailConfirmed || false,
          verification_code: newTeam.verificationCode || null,
          verification_code_sent_at: newTeam.verificationCodeSentAt || null,
          public_description: newTeam.publicDescription || null,
          public_skills: newTeam.publicSkills || [],
          indicative_daily_rate: newTeam.indicativeDailyRate || null,
          geographical_zone: newTeam.geographicalZone || null,
          pwa_installed: newTeam.pwaInstalled || false,
          fcm_token: newTeam.fcmToken || null,
          member_count: memberCount,
          category_ids: newTeam.categoryIds || [],
          is_category_supervisor: newTeam.isCategorySupervisor || false,
          assigned_team_id: newTeam.assignedTeamId || null,
        }).then(({ error }) => {
          if (error) console.error('[addTeam] Database Insert Error:', error.message, error.details);
        });
        // Auto-create sub-members for artist groups
        if (memberCount > 1) {
          for (let i = 1; i <= memberCount; i++) {
            const memberId = uuidv4();
            const memberName = `${newTeam.name} — Membre ${i}`;
            const member = {
              ...s, id: memberId, orgId, name: memberName,
              isArtist: true, memberCount: 1, parentArtistId: id, memberIndex: i,
              teamToken: undefined, linkedWalletQr: null,
            };
            set((state) => ({ team: [...state.team, member] }));
            supabase.from('staff').insert({
              id: memberId, name: memberName, role: s.role, roles: s.roles, color: s.color,
              email: '', phone: '', category_id: s.categoryId || null,
              access_password: s.accessPassword, is_on_duty: false,
              photo_url: s.photo, org_id: orgId,
              is_artist: true,
              member_count: 1, parent_artist_id: id, member_index: i,
            }).then(({ error }) => {
              if (error) console.error('[addTeam] Sub-member insert error:', error.message);
            });
          }
        }
        return id;
      },
      updateTeam: (id, updates) => {
        set((state) => ({ team: state.team.map(s => s.id === id ? { ...s, ...updates } : s) }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.role !== undefined) dbUpdates.role = updates.role;
        if (updates.roles !== undefined) dbUpdates.roles = updates.roles;
        if (updates.color !== undefined) dbUpdates.color = updates.color;
        if (updates.email !== undefined) dbUpdates.email = updates.email;
        if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
        if (updates.categoryId !== undefined) dbUpdates.category_id = updates.categoryId || null;
        if (updates.teamToken !== undefined) dbUpdates.staff_token = updates.teamToken;
        if (updates.accessPassword !== undefined) dbUpdates.access_password = updates.accessPassword;
        if (updates.isOnDuty !== undefined) dbUpdates.is_on_duty = updates.isOnDuty;
        if (updates.photo !== undefined) dbUpdates.photo_url = updates.photo;
        if (updates.unavailableDates !== undefined) dbUpdates.unavailable_dates = updates.unavailableDates;
        if (updates.customButtons !== undefined) dbUpdates.custom_buttons = updates.customButtons;
        if (updates.status !== undefined) dbUpdates.status = updates.status;
        if (updates.isDistributor !== undefined) dbUpdates.is_distributor = updates.isDistributor;
        if (updates.distAllowScan !== undefined) dbUpdates.dist_allow_scan = updates.distAllowScan;
        if (updates.distAllowDirect !== undefined) dbUpdates.dist_allow_direct = updates.distAllowDirect;
        if (updates.distAllowCheckin !== undefined) dbUpdates.dist_allow_checkin = updates.distAllowCheckin;
        if (updates.distAllowLinkWallet !== undefined) dbUpdates.dist_allow_link_wallet = updates.distAllowLinkWallet;
        if (updates.distAllowView !== undefined) dbUpdates.dist_allow_view = updates.distAllowView;
        if (updates.isArtist !== undefined) dbUpdates.is_artist = updates.isArtist;
        if (updates.isProfileVisible !== undefined) dbUpdates.is_profile_visible = updates.isProfileVisible;
        if (updates.emailConfirmed !== undefined) dbUpdates.email_confirmed = updates.emailConfirmed;
        if (updates.verificationCode !== undefined) dbUpdates.verification_code = updates.verificationCode;
        if (updates.verificationCodeSentAt !== undefined) dbUpdates.verification_code_sent_at = updates.verificationCodeSentAt;
        if (updates.publicDescription !== undefined) dbUpdates.public_description = updates.publicDescription;
        if (updates.publicSkills !== undefined) dbUpdates.public_skills = updates.publicSkills;
        if (updates.indicativeDailyRate !== undefined) dbUpdates.indicative_daily_rate = updates.indicativeDailyRate;
        if (updates.geographicalZone !== undefined) dbUpdates.geographical_zone = updates.geographicalZone;
        if (updates.orgId !== undefined) dbUpdates.org_id = updates.orgId;
        if (updates.pwaInstalled !== undefined) dbUpdates.pwa_installed = updates.pwaInstalled;
        if (updates.fcmToken !== undefined) dbUpdates.fcm_token = updates.fcmToken;
        if (updates.linkedWalletQr !== undefined) dbUpdates.linked_wallet_qr = updates.linkedWalletQr;
        if (updates.memberCount !== undefined) dbUpdates.member_count = updates.memberCount;
        if (updates.parentArtistId !== undefined) dbUpdates.parent_artist_id = updates.parentArtistId;
        if (updates.memberIndex !== undefined) dbUpdates.member_index = updates.memberIndex;
        if (updates.categoryIds !== undefined) dbUpdates.category_ids = updates.categoryIds;
        if (updates.isCategorySupervisor !== undefined) dbUpdates.is_category_supervisor = updates.isCategorySupervisor;
        if (updates.assignedTeamId !== undefined) dbUpdates.assigned_team_id = updates.assignedTeamId;
        if (updates.disableMessageEmails !== undefined) dbUpdates.disable_message_emails = updates.disableMessageEmails;
        supabase.from('staff').update(dbUpdates).eq('id', id).then(({ error }) => {
          if (error) console.error('[updateTeam] Database Update Error:', error.message, error.details);
        });
        // Handle memberCount increase: create missing sub-members
        if (updates.memberCount !== undefined) {
          const state = useStore.getState();
          const parent = state.team.find(t => t.id === id);
          if (parent && parent.isArtist && updates.memberCount > 1) {
            const existingMembers = state.team.filter(t => t.parentArtistId === id);
            const existingCount = existingMembers.length;
            if (updates.memberCount > existingCount) {
              for (let i = existingCount + 1; i <= updates.memberCount; i++) {
                const memberId = uuidv4();
                const memberName = `${parent.name} — Membre ${i}`;
                const member: Team = {
                  ...parent, id: memberId, name: memberName,
                  isArtist: true, memberCount: 1, parentArtistId: id, memberIndex: i,
                  teamToken: undefined, linkedWalletQr: null, email: '', phone: '',
                };
                set((st) => ({ team: [...st.team, member] }));
                supabase.from('staff').insert({
                  id: memberId, name: memberName, role: parent.role, roles: parent.roles, color: parent.color,
                  email: '', phone: '', category_id: parent.categoryId || null,
                  access_password: parent.accessPassword, is_on_duty: false,
                  photo_url: parent.photo, org_id: parent.orgId,
                  is_artist: true,
                  member_count: 1, parent_artist_id: id, member_index: i,
                }).then(({ error }) => {
                  if (error) console.error('[updateTeam] Sub-member insert error:', error.message);
                });
              }
            }
          }
        }
      },
      deleteTeam: (id) => {
        const state = useStore.getState();
        const target = state.team.find(t => t.id === id);
        // Also delete sub-members if this is a parent artist group
        const subMemberIds = target?.isArtist && (target.memberCount ?? 1) > 1
          ? state.team.filter(t => t.parentArtistId === id).map(t => t.id)
          : [];
        const allIds = [id, ...subMemberIds];
        set((st) => ({
          team: st.team.filter(s => !allIds.includes(s.id)),
          shifts: st.shifts.filter(sh => !allIds.includes(sh.teamId))
        }));
        allIds.forEach(delId => {
          supabase.from('staff').update({ deleted_at: new Date().toISOString() }).eq('id', delId).then();
        });
      },

      addPoint: (p) => {
        const id = p.id || uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newPoint = { ...p, id, orgId, projectId: projectId || undefined, isMapFocus: true };
        set((state) => ({ points: [...state.points, newPoint] }));
        supabase.from('points').insert({
          id, name: newPoint.name, category_id: newPoint.categoryId, icon_name: newPoint.iconName,
          location_lat: newPoint.location?.lat, location_lng: newPoint.location?.lng, required_staff: newPoint.requiredTeam,
          is_map_focus: true, description_markdown: newPoint.descriptionMarkdown ?? null, org_id: orgId,
          project_id: projectId,
          is_restricted: newPoint.isRestricted || false,
          is_stage: newPoint.isStage || false
        }).then();
        // Also insert opening hours
        newPoint.openingHours.forEach(oh => {
           supabase.from('point_opening_hours').insert({
              point_id: id, start_time: oh.start, end_time: oh.end, required_staff: oh.requiredTeam
           }).then();
        });
      },
      updatePoint: (id, updates) => {
        set((state) => ({ points: state.points.map(p => p.id === id ? { ...p, ...updates } : p) }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.categoryId !== undefined) dbUpdates.category_id = updates.categoryId;
        if (updates.iconName !== undefined) dbUpdates.icon_name = updates.iconName;
        if (updates.location !== undefined) {
           dbUpdates.location_lat = updates.location?.lat;
           dbUpdates.location_lng = updates.location?.lng;
        }
        if (updates.requiredTeam !== undefined) dbUpdates.required_staff = updates.requiredTeam;
        if (updates.swapEnabled !== undefined) dbUpdates.swap_enabled = updates.swapEnabled;
        if (updates.isMealPoint !== undefined) dbUpdates.is_meal_point = updates.isMealPoint;
        if (updates.unavailabilityEnabled !== undefined) dbUpdates.unavailability_enabled = updates.unavailabilityEnabled;
        if (updates.isMapFocus !== undefined) dbUpdates.is_map_focus = updates.isMapFocus;
        if (updates.isRestricted !== undefined) dbUpdates.is_restricted = updates.isRestricted;
        if (updates.isStage !== undefined) dbUpdates.is_stage = updates.isStage;
        if ('descriptionMarkdown' in updates) dbUpdates.description_markdown = updates.descriptionMarkdown ?? null;
        
        if (Object.keys(dbUpdates).length > 0) {
          supabase.from('points').update(dbUpdates).eq('id', id).then();
        }
        
        // Handling opening hours updates correctly would require diffing.
        // For MVP, we delete old hours and insert new ones if they changed.
        if (updates.openingHours) {
           supabase.from('point_opening_hours').delete().eq('point_id', id).then(() => {
              updates.openingHours!.forEach(oh => {
                 supabase.from('point_opening_hours').insert({
                    point_id: id, start_time: oh.start, end_time: oh.end, required_staff: oh.requiredTeam
                 }).then();
              });
           });

           // Clean up orphan shifts that no longer fall within any opening hour
           const newHours = updates.openingHours;
           const state = useStore.getState();
           const orphanShiftIds: string[] = [];
           state.shifts.forEach(sh => {
             if (sh.pointId !== id) return;
             const shStart = new Date(sh.start + (sh.start.endsWith('Z') ? '' : 'Z'));
             const shEnd = new Date(sh.end + (sh.end.endsWith('Z') ? '' : 'Z'));
             const hasSlot = newHours.some(oh => {
               const slotStart = new Date(oh.start + (oh.start.endsWith('Z') ? '' : 'Z'));
               const slotEnd = new Date(oh.end + (oh.end.endsWith('Z') ? '' : 'Z'));
               return shStart < slotEnd && shEnd > slotStart;
             });
             if (!hasSlot) orphanShiftIds.push(sh.id);
           });

           if (orphanShiftIds.length > 0) {
             const now = new Date().toISOString();
             set((s) => ({
               shifts: s.shifts.filter(sh => !orphanShiftIds.includes(sh.id))
             }));
             supabase.from('shifts').update({ deleted_at: now }).in('id', orphanShiftIds).then();
           }
        }
      },
      deletePoint: (id) => {
        set((state) => ({
          points: state.points.filter(p => p.id !== id),
          shifts: state.shifts.filter(sh => sh.pointId !== id)
        }));
        supabase.from('points').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },

      addTask: (t) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newTask: Task = { ...t, id, orgId, projectId: projectId || undefined };
        set((s) => ({ tasks: [...s.tasks, newTask] }));
        supabase.from('tasks').insert({
          id, name: newTask.name, category_id: newTask.categoryId, org_id: orgId,
          project_id: projectId, required_staff: newTask.requiredTeam,
          icon_name: newTask.iconName, description_markdown: newTask.descriptionMarkdown ?? null,
        }).then();
        // Insert opening hours
        newTask.openingHours.forEach(oh => {
          supabase.from('task_opening_hours').insert({
            task_id: id, start_time: oh.start, end_time: oh.end, required_staff: oh.requiredTeam || 1, is_hard: oh.isHard || false,
          }).then();
        });
      },

      updateTask: (id, updates) => {
        set((s) => ({ tasks: s.tasks.map(t => t.id === id ? { ...t, ...updates } : t) }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.categoryId !== undefined) dbUpdates.category_id = updates.categoryId;
        if (updates.requiredTeam !== undefined) dbUpdates.required_staff = updates.requiredTeam;
        if (updates.iconName !== undefined) dbUpdates.icon_name = updates.iconName;
        if ('descriptionMarkdown' in updates) dbUpdates.description_markdown = updates.descriptionMarkdown ?? null;
        if (Object.keys(dbUpdates).length > 0) {
          supabase.from('tasks').update(dbUpdates).eq('id', id).then();
        }
        if (updates.openingHours) {
          supabase.from('task_opening_hours').delete().eq('task_id', id).then(() => {
            updates.openingHours!.forEach(oh => {
              supabase.from('task_opening_hours').insert({
                task_id: id, start_time: oh.start, end_time: oh.end, required_staff: oh.requiredTeam || 1, is_hard: oh.isHard || false,
              }).then();
            });
          });
        }
      },

      deleteTask: (id) => {
        set((s) => ({
          tasks: s.tasks.filter(t => t.id !== id),
          shifts: s.shifts.filter(sh => sh.taskId !== id),
        }));
        supabase.from('tasks').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },

      addShift: (s) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newShift = { ...s, id, orgId, projectId: projectId || undefined };
        set((state) => ({ shifts: [...state.shifts, newShift] }));
        supabase.from('shifts').insert({
          id, staff_id: newShift.teamId, point_id: newShift.pointId || null, task_id: newShift.taskId || null, start_time: newShift.start, end_time: newShift.end, description: newShift.description, org_id: orgId, project_id: projectId, shift_type: newShift.shiftType || 'standard',
          proposed_amount: newShift.proposedAmount || null,
          agreed_amount: newShift.agreedAmount || null,
          amount_negotiated_by_staff: newShift.amountNegotiatedByTeam || false,
          proposed_currency: newShift.proposedCurrency || 'TND',
          agreement_status: newShift.agreementStatus || 'approved'
        }).then();
        // Auto-add point's category to team member's categoryIds
        if (newShift.pointId) {
          const point = state.points.find(p => p.id === newShift.pointId);
          if (point?.categoryId) {
            const member = state.team.find(t => t.id === newShift.teamId);
            const currentIds = new Set(member?.categoryIds || (member?.categoryId ? [member.categoryId] : []));
            if (!currentIds.has(point.categoryId)) {
              currentIds.add(point.categoryId);
              const updatedIds = Array.from(currentIds);
              set((st) => ({ team: st.team.map(t => t.id === newShift.teamId ? { ...t, categoryIds: updatedIds } : t) }));
              supabase.from('staff').update({ category_ids: updatedIds }).eq('id', newShift.teamId).then();
            }
          }
        }
      },
      updateShift: (id, updates) => {
        set((state) => ({ shifts: state.shifts.map(s => s.id === id ? { ...s, ...updates } : s) }));
        const dbUpdates: any = {};
        if (updates.teamId !== undefined) dbUpdates.staff_id = updates.teamId;
        if (updates.pointId !== undefined) dbUpdates.point_id = updates.pointId;
        if (updates.taskId !== undefined) dbUpdates.task_id = updates.taskId;
        if (updates.start !== undefined) dbUpdates.start_time = updates.start;
        if (updates.end !== undefined) dbUpdates.end_time = updates.end;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.shiftType !== undefined) dbUpdates.shift_type = updates.shiftType;
        if ('actualStart' in updates) dbUpdates.actual_start = updates.actualStart ?? null;
        if ('actualEnd' in updates) dbUpdates.actual_end = updates.actualEnd ?? null;
        if (updates.proposedAmount !== undefined) dbUpdates.proposed_amount = updates.proposedAmount;
        if (updates.agreedAmount !== undefined) dbUpdates.agreed_amount = updates.agreedAmount;
        if (updates.amountNegotiatedByTeam !== undefined) dbUpdates.amount_negotiated_by_staff = updates.amountNegotiatedByTeam;
        if (updates.proposedCurrency !== undefined) dbUpdates.proposed_currency = updates.proposedCurrency;
        if (updates.agreementStatus !== undefined) dbUpdates.agreement_status = updates.agreementStatus;
        supabase.from('shifts').update(dbUpdates).eq('id', id).then();
      },
      deleteShift: (id) => {
        set((state) => ({ shifts: state.shifts.filter(s => s.id !== id) }));
        supabase.from('shifts').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },

      addIssue: (issue) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newIssue = { ...issue, id, orgId, projectId: projectId || undefined, timestamp: new Date().toISOString(), resolved: false };
        set((state) => ({ issues: [...state.issues, newIssue] }));
        // Use SECURITY DEFINER RPC to bypass RLS (anonymous sessions can't insert directly)
        supabase.rpc('insert_issue', {
          p_id: id, p_point_id: newIssue.pointId, p_staff_id: newIssue.teamId,
          p_type: newIssue.type || 'alert', p_description: newIssue.message,
          p_org_id: orgId, p_project_id: projectId || null
        }).then(({ error }) => {
          if (error) console.error('[addIssue] RPC Error:', error.message);
        });
      },
      resolveIssue: (id) => {
        set((state) => ({ issues: state.issues.map(i => i.id === id ? { ...i, resolved: true } : i) }));
        supabase.from('issues').update({ resolved: true }).eq('id', id).then();
      },
      
            addMessage: (teamId, content, senderName, imageUrl = null, voiceUrl = null) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        let senderEmail = '';
        if (state.currentUser.role === 'admin') {
          const org = state.organizations.find(o => o.id === orgId);
          senderEmail = org?.adminEmail || 'benmloukaamen@gmail.com';
        } else if (state.currentUser.role === 'manager' && state.currentUser.categoryId) {
          const cat = state.categories.find(c => c.id === state.currentUser.categoryId);
          senderEmail = cat?.managerEmail || '';
        }
        // Find sender's team ID for proper routing (so staff can see who sent the message)
        const senderTeamId = senderEmail
          ? state.team.find(s => s.email?.toLowerCase().trim() === senderEmail.toLowerCase().trim())?.id
          : null;
        const newMessage: TeamMessage = { id, teamId, content, orgId, projectId: projectId || undefined, timestamp: new Date().toISOString(), read: false, fromTeam: false, senderName, recipientTeamId: senderTeamId || undefined, imageUrl, voiceUrl };
        set((s) => ({ messages: [...(s.messages || []), newMessage] }));
        supabase.from('messages').insert({
          id, staff_id: teamId, content, read: false, from_staff: false, sender_name: senderName ?? null, sender_email: senderEmail || null, recipient_staff_id: senderTeamId || null, org_id: orgId, project_id: projectId, image_url: imageUrl, voice_url: voiceUrl
        }).then();
        // Les notifications push et email sont déclenchées automatiquement
        // par le trigger PostgreSQL sur la table messages
      },
            addReply: (teamId, content, senderName, imageUrl = null, voiceUrl = null) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        const newMessage: TeamMessage = { id, teamId, content, orgId: orgId || undefined, projectId: projectId || undefined, timestamp: new Date().toISOString(), read: false, fromTeam: true, senderName, imageUrl, voiceUrl };
        set((s) => ({ messages: [...(s.messages || []), newMessage] }));
        supabase.from('messages').insert({
          id, staff_id: teamId, content, read: false, from_staff: true, sender_name: senderName ?? null, org_id: orgId, project_id: projectId, image_url: imageUrl, voice_url: voiceUrl
        }).then();
      },
            addBroadcastMessage: (teamIds, content, senderLabel) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        // Déterminer l'email de l'expéditeur
        let senderEmail = '';
        if (state.currentUser.role === 'admin') {
          const org = state.organizations.find(o => o.id === orgId);
          senderEmail = org?.adminEmail || 'benmloukaamen@gmail.com';
        } else if (state.currentUser.role === 'manager' && state.currentUser.categoryId) {
          const cat = state.categories.find(c => c.id === state.currentUser.categoryId);
          senderEmail = cat?.managerEmail || '';
        }
        // Insert one message per team member (each triggers its own push notification)
        const newMessages = teamIds.map(teamId => ({
          id: uuidv4(),
          teamId,
          content,
          orgId,
          projectId: projectId || undefined,
          timestamp: new Date().toISOString(),
          read: false,
          fromTeam: false,
          senderName: senderLabel,
        }));
        set((state) => ({ messages: [...(state.messages || []), ...newMessages] }));
        // Fire-and-forget DB inserts (each INSERT triggers Edge Function webhook)
        newMessages.forEach(m => {
          supabase.from('messages').insert({
            id: m.id, staff_id: m.teamId, content, read: false, from_staff: false, sender_name: senderLabel, sender_email: senderEmail || null, org_id: orgId, project_id: projectId
          }).then();
        });
      },
      markMessageRead: (id) => {
        set((state) => ({ messages: (state.messages || []).map(m => m.id === id ? { ...m, read: true } : m) }));
        // Use SECURITY DEFINER RPC to bypass RLS
        supabase.rpc('mark_message_read', { p_message_id: id }).then();
      },
      markAllMessagesRead: () => {
        const state = useStore.getState();
        // Mark ALL unread messages — admin wants to clear everything
        const unreadIds = (state.messages || []).filter(m => !m.read).map(m => m.id);
        if (unreadIds.length === 0) return;

        set((state) => ({
          messages: (state.messages || []).map(m => unreadIds.includes(m.id) ? { ...m, read: true } : m)
        }));

        // Batch update in Supabase via RPC if available, or individual calls
        unreadIds.forEach(id => {
          supabase.rpc('mark_message_read', { p_message_id: id }).then();
        });
      },
      openReply: (teamId) => {
        set({ messagingDrawer: { isOpen: true, selectedTeamId: teamId } });
      },
      setMessagingDrawerOpen: (isOpen) => {
        set((state) => ({ messagingDrawer: { ...state.messagingDrawer, isOpen } }));
      },
      markTeamMessagesRead: (teamId) => {
        const state = useStore.getState();
        // Mark unread messages for this team, both fromTeam (team→admin) and toTeam (admin→team)
        const unreadIds = (state.messages || []).filter(m =>
          !m.read && m.teamId === teamId && (m.fromTeam || !m.fromTeam)
        ).map(m => m.id);
        if (unreadIds.length === 0) return;

        set((state) => ({
          messages: (state.messages || []).map(m => unreadIds.includes(m.id) ? { ...m, read: true } : m)
        }));

        unreadIds.forEach(id => {
          supabase.rpc('mark_message_read', { p_message_id: id }).then();
        });
      },

      // ── Approval Request Actions ────────────────────────────

      setApprovalRequests: (requests) => set({ approvalRequests: requests }),

      createApprovalRequest: (request) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId || request.orgId;
        const projectId = state.currentProjectId || request.projectId;
        if (!orgId) return;
        const expiresAt = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
        const newRequest: MessageApprovalRequest = {
          ...request,
          id,
          orgId,
          projectId: projectId || undefined,
          status: 'pending',
          createdAt: new Date().toISOString(),
          expiresAt,
        };
        set((s) => ({ approvalRequests: [...(s.approvalRequests || []), newRequest] }));
        supabase.from('message_approval_requests').insert({
          id,
          staff_id: newRequest.teamId,
          category_id: newRequest.categoryId || null,
          org_id: orgId,
          project_id: projectId || null,
          content: newRequest.content,
          image_url: newRequest.imageUrl || null,
          voice_url: newRequest.voiceUrl || null,
          recipient_ids: newRequest.recipientIds,
          recipient_names: newRequest.recipientNames,
          recipient_count: newRequest.recipientIds.length,
          status: 'pending',
          expires_at: expiresAt,
          created_at: newRequest.createdAt,
        }).then(({ error }) => {
          if (error) console.error('[createApprovalRequest] Error:', error.message);
        });
      },

      approveRequest: (id, approvedBy) => {
        const state = useStore.getState();
        const request = (state.approvalRequests || []).find(r => r.id === id);
        if (!request || request.status !== 'pending') return;

        // Mark as approved in state
        set((s) => ({
          approvalRequests: (s.approvalRequests || []).map(r =>
            r.id === id ? { ...r, status: 'approved' as const } : r
          )
        }));

        // Send messages to all recipients
        const store = useStore.getState();
        store.addTeamDirectMessages(
          request.recipientIds,
          request.content,
          request.teamName,
          request.teamId,
          request.imageUrl || null,
          request.voiceUrl || null
        );

        // Update DB
        supabase.from('message_approval_requests').update({
          status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date().toISOString(),
        }).eq('id', id).then(({ error }) => {
          if (error) console.error('[approveRequest] Error:', error.message);
        });
      },

      rejectRequest: (id) => {
        set((s) => ({
          approvalRequests: (s.approvalRequests || []).map(r =>
            r.id === id ? { ...r, status: 'rejected' as const } : r
          )
        }));
        supabase.from('message_approval_requests').update({
          status: 'rejected',
          rejected_at: new Date().toISOString(),
        }).eq('id', id).then(({ error }) => {
          if (error) console.error('[rejectRequest] Error:', error.message);
        });
      },

      relanceRequest: (id) => {
        // Update the expiresAt to reset the 4h timer and re-trigger notifications
        const newExpiresAt = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
        set((s) => ({
          approvalRequests: (s.approvalRequests || []).map(r =>
            r.id === id ? { ...r, expiresAt: newExpiresAt } : r
          )
        }));
        supabase.from('message_approval_requests').update({
          expires_at: newExpiresAt,
        }).eq('id', id).then(({ error }) => {
          if (error) console.error('[relanceRequest] Error:', error.message);
        });
        // Also try to re-send push notification via edge function
        const state = useStore.getState();
        const request = (state.approvalRequests || []).find(r => r.id === id);
        if (request) {
          // Fire-and-forget reload of the DB trigger by doing a dummy update
          // The notification will be handled by the edge function call
          supabase.functions.invoke('send-push-notification', {
            body: {
              record: {
                staff_id: request.teamId,
                content: `📋 Demande relancée: ${request.content}`,
                sender_name: `Relance: ${request.teamName} (${request.recipientIds.length} destinataires)`,
              },
              table: 'message_approval_requests',
              approval_id: id,
              category_id: request.categoryId,
              org_id: request.orgId,
            }
          }).then(({ error }) => {
            if (error) console.error('[relanceRequest] Push error:', error.message);
          });
        }
      },

      cancelRequest: (id) => {
        set((s) => ({
          approvalRequests: (s.approvalRequests || []).filter(r => r.id !== id)
        }));
        supabase.from('message_approval_requests').update({
          deleted_at: new Date().toISOString(),
        }).eq('id', id).then(({ error }) => {
          if (error) console.error('[cancelRequest] Error:', error.message);
        });
      },

      addTeamDirectMessages: (teamIds, content, senderName, _senderTeamId, imageUrl = null, voiceUrl = null) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        // Use the sender's team ID so messages appear under the correct team member card
        const effectiveTeamId = _senderTeamId || teamIds[0];

        // Insert one message per recipient (each stores recipientTeamId for per-admin notification filtering)
        const newMessages = teamIds.map(recipientId => ({
          id: uuidv4(),
          teamId: effectiveTeamId,
          recipientTeamId: recipientId,
          content,
          orgId,
          imageUrl,
          voiceUrl,
          projectId: projectId || undefined,
          timestamp: new Date().toISOString(),
          read: false,
          fromTeam: true,
          senderName,
        }));
        set((s) => ({ messages: [...(s.messages || []), ...newMessages] }));
        // Fire-and-forget DB inserts
        newMessages.forEach(m => {
          supabase.from('messages').insert({
            id: m.id,
            staff_id: m.teamId,
            content: m.content,
            read: false,
            from_staff: true,
            sender_name: m.senderName,
            sender_email: null,
            org_id: m.orgId,
            project_id: m.projectId,
            image_url: m.imageUrl,
            voice_url: m.voiceUrl,
            recipient_staff_id: m.recipientTeamId,
          }).then(({ error }) => {
            if (error) console.error('[addTeamDirectMessages] Error:', error.message);
          });
        });
      },

      addOrganization: (name) => {
        const id = uuidv4();
        const adminToken = uuidv4();
        const newOrg = { 
          id, name, adminToken, 
          allowAdminSettings: false,
          settings: { minRestTimeHours: 8 }, 
          createdAt: new Date().toISOString() 
        } as any;
        set((state) => ({ organizations: [...state.organizations, newOrg] }));
        supabase.from('organizations').insert({ id, name, admin_token: adminToken }).then();
      },
      setCurrentOrgId: (id) => {
        set({ currentOrgId: id });
        if (id) {
          localStorage.setItem('wfm_current_org_id', id);
        } else {
          localStorage.removeItem('wfm_current_org_id');
        }
      },
      
      addProject: async (name, startDate, endDate) => {
        const id = uuidv4();
        const orgId = useStore.getState().currentOrgId;
        if (!orgId) return id;
        const newProject: Project = {
          id,
          orgId,
          name,
          startDate,
          endDate,
          createdAt: new Date().toISOString()
        };
        set((state) => ({ projects: [...state.projects, newProject] }));
        await supabase.from('projects').insert({
          id,
          org_id: orgId,
          name,
          start_date: startDate || null,
          end_date: endDate || null
        });
        return id;
      },
      updateProject: (id, updates) => {
        set((state) => ({
          projects: state.projects.map(p => p.id === id ? { ...p, ...updates } : p)
        }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate || null;
        if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate || null;
        supabase.from('projects').update(dbUpdates).eq('id', id).then();
      },
      deleteProject: (id) => {
        set((state) => ({
          projects: state.projects.filter(p => p.id !== id),
          categories: state.categories.filter(c => c.projectId !== id),
          points: state.points.filter(p => p.projectId !== id),
          shifts: state.shifts.filter(s => s.projectId !== id),
          issues: state.issues.filter(i => i.projectId !== id),
          messages: state.messages.filter(m => m.projectId !== id),
          offerProducts: state.offerProducts.filter(o => o.projectId !== id),
          customQuotas: state.customQuotas.filter(q => q.projectId !== id),
          consumptions: state.consumptions.filter(c => c.projectId !== id),
          checkins: state.checkins.filter(ch => ch.projectId !== id),
        }));
        supabase.from('projects').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },
      setCurrentProjectId: (id) => {
        set({ currentProjectId: id });
        if (id) {
          localStorage.setItem('wfm_project_id', id);
        } else {
          localStorage.removeItem('wfm_project_id');
        }
      },
      duplicateProjectConfig: async (sourceProjectId, targetProjectId) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        if (!orgId) return;

        const sourceCats = state.categories.filter(c => c.projectId === sourceProjectId);
        const sourcePts = state.points.filter(p => p.projectId === sourceProjectId);

        const catIdMap: Record<string, string> = {};
        const catInserts: any[] = [];
        const clonedCats: Category[] = [];

        for (const cat of sourceCats) {
          const newCatId = uuidv4();
          catIdMap[cat.id] = newCatId;
          const managerToken = uuidv4();
          clonedCats.push({
            ...cat,
            id: newCatId,
            projectId: targetProjectId,
            managerToken
          });
          catInserts.push({
            id: newCatId,
            org_id: orgId,
            project_id: targetProjectId,
            name: cat.name,
            is_general: cat.isGeneral || false,
            is_artist_category: cat.isArtistCategory || false,
            is_public: cat.isPublic || false,
            manager_token: managerToken,
            manager_emails: cat.managerEmails || []
          });
        }

        if (catInserts.length > 0) {
          await supabase.from('categories').insert(catInserts);
        }

        const clonedPts: OperationalPoint[] = [];
        const ptInserts: any[] = [];
        const openingHoursInserts: any[] = [];

        for (const pt of sourcePts) {
          const newPtId = uuidv4();
          const targetCatId = pt.categoryId ? catIdMap[pt.categoryId] : null;
          
          clonedPts.push({
            ...pt,
            id: newPtId,
            categoryId: targetCatId || undefined,
            projectId: targetProjectId,
          });

          ptInserts.push({
            id: newPtId,
            org_id: orgId,
            project_id: targetProjectId,
            category_id: targetCatId,
            name: pt.name,
            required_staff: pt.requiredTeam,
            location_lat: pt.location?.lat,
            location_lng: pt.location?.lng,
            icon_name: pt.iconName,
            description_markdown: pt.descriptionMarkdown || null,
            swap_enabled: pt.swapEnabled || false,
            unavailability_enabled: pt.unavailabilityEnabled || false,
            is_meal_point: pt.isMealPoint || false,
            is_map_focus: pt.isMapFocus || false,
            is_restricted: pt.isRestricted || false,
            is_stage: pt.isStage || false
          });

          if (pt.openingHours) {
            for (const slot of pt.openingHours) {
              openingHoursInserts.push({
                point_id: newPtId,
                start_time: slot.start,
                end_time: slot.end,
                required_staff: slot.requiredTeam || 1
              });
            }
          }
        }

        if (ptInserts.length > 0) {
          await supabase.from('points').insert(ptInserts);
        }

        if (openingHoursInserts.length > 0) {
          await supabase.from('point_opening_hours').insert(openingHoursInserts);
        }

        set((state) => ({
          categories: [...state.categories, ...clonedCats],
          points: [...state.points, ...clonedPts],
        }));
      },

      importPlaces: async (places, options) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return { created: 0, updated: 0, error: 'Aucune organisation active.' };
        if (places.length === 0) return { created: 0, updated: 0, error: null };

        // Un lieu déjà présent dans le projet (même nom normalisé) est mis à jour
        // plutôt que dupliqué : réimporter un programme corrigé reste sans effet de bord.
        const existingByKey = new Map(
          state.points
            .filter(p => p.projectId === projectId)
            .map(p => [normalizeString(p.name), p])
        );

        const categoryId = options.categoryId || null;
        const pointInserts: Record<string, unknown>[] = [];
        const pointUpdates: { id: string; payload: Record<string, unknown> }[] = [];
        const pointsToClear: string[] = [];
        const hourInserts: Record<string, unknown>[] = [];
        const newPoints: OperationalPoint[] = [];
        const refreshed: { id: string; categoryId?: string; requiredTeam: number; descriptionMarkdown: string; openingHours: TimeSlot[] }[] = [];
        let created = 0;
        let updated = 0;

        for (const place of places) {
          const openingHours: TimeSlot[] = place.slots.map(s => ({
            start: s.start,
            end: s.end,
            requiredTeam: s.requiredTeam,
          }));
          const requiredTeam = openingHours.reduce((max, slot) => Math.max(max, slot.requiredTeam ?? 1), 1);
          const existing = existingByKey.get(place.key);
          const pointId = existing ? existing.id : uuidv4();

          if (existing) {
            updated++;
            pointsToClear.push(pointId);
            pointUpdates.push({
              id: pointId,
              payload: {
                category_id: categoryId,
                required_staff: requiredTeam,
                description_markdown: place.description,
              },
            });
          } else {
            created++;
            pointInserts.push({
              id: pointId,
              org_id: orgId,
              project_id: projectId,
              category_id: categoryId,
              name: place.name,
              required_staff: requiredTeam,
              icon_name: 'MapPin',
              description_markdown: place.description,
              is_map_focus: true,
              is_restricted: false,
              is_stage: false,
            });
            newPoints.push({
              id: pointId,
              name: place.name,
              orgId,
              projectId: projectId || undefined,
              categoryId: categoryId ?? undefined,
              requiredTeam,
              openingHours,
              iconName: 'MapPin',
              descriptionMarkdown: place.description,
              isMapFocus: true,
              isRestricted: false,
              isStage: false,
            });
          }

          refreshed.push({
            id: pointId,
            categoryId: categoryId ?? undefined,
            requiredTeam,
            descriptionMarkdown: place.description,
            openingHours,
          });

          openingHours.forEach(h => hourInserts.push({
            point_id: pointId,
            start_time: h.start,
            end_time: h.end,
            required_staff: h.requiredTeam ?? 1,
          }));
        }

        try {
          if (pointInserts.length > 0) {
            const { error } = await supabase.from('points').insert(pointInserts);
            if (error) return { created: 0, updated: 0, error: error.message };
          }
          for (const update of pointUpdates) {
            const { error } = await supabase.from('points').update(update.payload).eq('id', update.id);
            if (error) return { created: 0, updated: 0, error: error.message };
          }
          if (pointsToClear.length > 0) {
            const { error } = await supabase.from('point_opening_hours').delete().in('point_id', pointsToClear);
            if (error) return { created: 0, updated: 0, error: error.message };
          }
          if (hourInserts.length > 0) {
            const { error } = await supabase.from('point_opening_hours').insert(hourInserts);
            if (error) return { created: 0, updated: 0, error: error.message };
          }
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          return { created: 0, updated: 0, error: `${message} — relancez l'import pour réessayer.` };
        }

        set((s) => {
          const byId = new Map(refreshed.map(r => [r.id, r]));
          const merged = s.points.map(p => {
            const r = byId.get(p.id);
            if (!r) return p;
            return {
              ...p,
              categoryId: r.categoryId ?? p.categoryId,
              requiredTeam: r.requiredTeam,
              descriptionMarkdown: r.descriptionMarkdown,
              openingHours: r.openingHours,
            };
          });
          return { points: [...merged, ...newPoints] };
        });

        return { created, updated, error: null };
      },

      setImpersonatedTeamId: (id) => set({ impersonatedTeamId: id }),
      setViewAsManagerCategoryId: (id) => set({ viewAsManagerCategoryId: id }),

      addOfferProduct: async (product, categoryQuotas) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const embeddedQuotas = (categoryQuotas && categoryQuotas.length > 0) ? categoryQuotas : undefined;
        const newProduct = { ...product, id, orgId, projectId: projectId || undefined, categoryQuotas: embeddedQuotas };
        set((state) => ({ offerProducts: [...state.offerProducts, newProduct] }));
        // Insert product and category quotas — await both to ensure persistence before sync overwrites
        const { error: prodError } = await supabase.from('offer_products').insert({
          id, name: product.name, icon: product.icon, default_quota: product.defaultQuota, points_value: product.pointsValue, org_id: orgId, project_id: projectId
        });
        if (prodError) console.error('[addOfferProduct] product insert error:', prodError);
        if (categoryQuotas && categoryQuotas.length > 0) {
          const catRows = categoryQuotas.map(cq => ({
            id: uuidv4(),
            product_id: id,
            category_id: cq.categoryId,
            quota: cq.quota,
            org_id: orgId,
            project_id: projectId,
          }));
          set((state) => ({
            offerProductCategoryQuotas: [...state.offerProductCategoryQuotas, ...catRows.map(r => ({
              id: r.id,
              productId: r.product_id,
              categoryId: r.category_id,
              quota: r.quota,
              orgId: r.org_id,
              projectId: r.project_id,
            }))]
          }));
          const { error: catError } = await supabase.from('offer_product_category_quotas').insert(catRows);
          if (catError) {
            console.error('[addOfferProduct] category quota insert error:', catError.message, catError.details, catError.hint);
            alert('Erreur lors de l\'enregistrement des quotas par catégorie: ' + catError.message);
          }
        }
      },
      setProductCategoryQuota: (productId, categoryId, quota) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const id = uuidv4();
        set((state) => {
          const filtered = state.offerProductCategoryQuotas.filter(q => !(q.productId === productId && q.categoryId === categoryId));
          return { offerProductCategoryQuotas: [...filtered, { id, productId, categoryId, quota, orgId, projectId: projectId || undefined }] };
        });
        supabase.from('offer_product_category_quotas').upsert({
          id, product_id: productId, category_id: categoryId, quota, org_id: orgId, project_id: projectId
        }, { onConflict: 'product_id,category_id' }).then();
      },
      deleteProductCategoryQuota: (productId, categoryId) => {
        set((state) => ({
          offerProductCategoryQuotas: state.offerProductCategoryQuotas.filter(q => !(q.productId === productId && q.categoryId === categoryId))
        }));
        supabase.from('offer_product_category_quotas').delete().eq('product_id', productId).eq('category_id', categoryId).then();
      },
      deleteOfferProduct: (id) => {
        set((state) => ({ offerProducts: state.offerProducts.filter(p => p.id !== id) }));
        supabase.from('offer_products').update({ deleted_at: new Date().toISOString() }).eq('id', id).then();
      },
      updateOfferProduct: (id, updates) => {
        set((state) => ({
          offerProducts: state.offerProducts.map(p => p.id === id ? { ...p, ...updates } : p)
        }));
        const dbUpdates: any = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
        if (updates.defaultQuota !== undefined) dbUpdates.default_quota = updates.defaultQuota;
        if (updates.pointsValue !== undefined) dbUpdates.points_value = updates.pointsValue;
        supabase.from('offer_products').update(dbUpdates).eq('id', id).then();
      },
      updateTeamDistributor: (teamId, rights) => {
        set((state) => ({
          team: state.team.map(s => s.id === teamId ? { ...s, ...rights } : s)
        }));
        const dbUpdates: any = {};
        if (rights.isDistributor !== undefined) dbUpdates.is_distributor = rights.isDistributor;
        if (rights.distAllowScan !== undefined) dbUpdates.dist_allow_scan = rights.distAllowScan;
        if (rights.distAllowDirect !== undefined) dbUpdates.dist_allow_direct = rights.distAllowDirect;
        if (rights.distAllowCheckin !== undefined) dbUpdates.dist_allow_checkin = rights.distAllowCheckin;
        if (rights.distAllowLinkWallet !== undefined) dbUpdates.dist_allow_link_wallet = rights.distAllowLinkWallet;
        if (rights.distAllowView !== undefined) dbUpdates.dist_allow_view = rights.distAllowView;
        supabase.from('staff').update(dbUpdates).eq('id', teamId).then();
      },
      setCustomQuota: (teamId, productName, quota) => {
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        set((state) => {
          const filtered = state.customQuotas.filter(q => !(q.teamId === teamId && q.productName === productName));
          const newQuota = { id: uuidv4(), teamId, productName, customQuota: quota, orgId, projectId: projectId || undefined };
          return { customQuotas: [...filtered, newQuota] };
        });
        // Upsert to Supabase
        supabase.from('staff_custom_quotas').upsert({
          staff_id: teamId, product_name: productName, custom_quota: quota, org_id: orgId, project_id: projectId
        }, { onConflict: 'staff_id,product_name' }).then();
      },
      addTeamConsumption: (consumption) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newConsumption = { ...consumption, id, orgId, projectId: projectId || undefined, createdAt: new Date().toISOString() };
        set((state) => ({ consumptions: [...state.consumptions, newConsumption] }));
        supabase.from('staff_consumptions').insert({
          id, staff_id: consumption.teamId, product_name: consumption.productName, quantity: consumption.quantity, points_value: consumption.pointsValue, scanned_by: consumption.scannedBy, point_id: consumption.pointId, org_id: orgId, project_id: projectId
        }).then();
      },
      deleteTeamConsumption: (id, deletedBy) => {
        const now = new Date().toISOString();
        set((state) => ({
          consumptions: state.consumptions.map(c => c.id === id ? { ...c, deletedAt: now, deletedBy } : c)
        }));
        supabase.from('staff_consumptions').update({ deleted_at: now, deleted_by: deletedBy }).eq('id', id).then();
      },
      addTeamCheckin: (checkin) => {
        const id = uuidv4();
        const state = useStore.getState();
        const orgId = state.currentOrgId;
        const projectId = state.currentProjectId;
        if (!orgId) return;
        const newCheckin = { ...checkin, id, orgId, projectId: projectId || undefined, createdAt: new Date().toISOString() };
        set((state) => ({ checkins: [...state.checkins, newCheckin] }));
        supabase.from('staff_checkins').insert({
          id, staff_id: checkin.teamId, point_id: checkin.pointId, scanned_by: checkin.scannedBy, org_id: orgId, project_id: projectId
        }).then();
      },
      deleteTeamCheckin: (id, deletedBy) => {
        const now = new Date().toISOString();
        set((state) => ({
          checkins: state.checkins.map(ch => ch.id === id ? { ...ch, deletedAt: now, deletedBy } : ch)
        }));
        supabase.from('staff_checkins').update({ deleted_at: now, deleted_by: deletedBy }).eq('id', id).then();
      },
      resetAllQuotas: (orgId) => {
        const nowStr = new Date().toISOString();
        set((state) => ({
          organizations: state.organizations.map(o => o.id === orgId ? { ...o, last_quota_reset: nowStr } : o)
        }));
        supabase.from('organizations').update({ last_quota_reset: nowStr }).eq('id', orgId).then();
      },
      scheduleQuotaReset: (orgId, dateTime) => {
        set((state) => ({
          organizations: state.organizations.map(o => o.id === orgId ? { ...o, scheduled_quota_reset: dateTime || undefined } : o)
        }));
        supabase.from('organizations').update({ scheduled_quota_reset: dateTime }).eq('id', orgId).then();
      },
      addSharedTeamLink: async (teamId, orgId) => {
        const previous = useStore.getState().sharedTeamOrganizations;
        set((state) => ({
          sharedTeamOrganizations: [
            ...state.sharedTeamOrganizations,
            { id: uuidv4(), teamId, orgId, createdAt: new Date().toISOString() }
          ]
        }));
        const { error } = await supabase.from('shared_staff_organizations').insert({ staff_id: teamId, org_id: orgId });
        if (error) {
          console.error('[addSharedTeamLink] Error:', error.message);
          // Roll back: never leave the UI showing a link that was not persisted
          set({ sharedTeamOrganizations: previous });
          return error.message;
        }
        return null;
      },
      removeSharedTeamLink: async (teamId, orgId) => {
        set((state) => ({
          sharedTeamOrganizations: state.sharedTeamOrganizations.filter(
            link => !(link.teamId === teamId && link.orgId === orgId)
          )
        }));
        const { error } = await supabase.from('shared_staff_organizations').delete().eq('staff_id', teamId).eq('org_id', orgId);
        if (error) console.error('[removeSharedTeamLink] Error:', error.message);
      },

      // ── Custom Field Definitions ────────────────────────────

      addCustomFieldDefinition: (field) => {
        const id = uuidv4();
        const orgId = useStore.getState().currentOrgId;
        if (!orgId) return;
        const newField: CustomFieldDefinition = {
          ...field,
          id,
          orgId,
          createdAt: new Date().toISOString()
        };
        set((state) => ({ customFieldDefinitions: [...state.customFieldDefinitions, newField] }));
        supabase.from('custom_field_definitions').insert({
          id, org_id: orgId, label: field.label, field_type: field.fieldType,
          options: field.options || [], required: field.required,
          sort_order: field.sortOrder,
        }).then(({ error }) => {
          if (error) console.error('[addCustomFieldDefinition] Error:', error.message);
        });
      },

      updateCustomFieldDefinition: (id, updates) => {
        set((state) => ({
          customFieldDefinitions: state.customFieldDefinitions.map(
            f => f.id === id ? { ...f, ...updates } : f
          )
        }));
        const dbUpdates: any = {};
        if (updates.label !== undefined) dbUpdates.label = updates.label;
        if (updates.fieldType !== undefined) dbUpdates.field_type = updates.fieldType;
        if (updates.options !== undefined) dbUpdates.options = updates.options;
        if (updates.required !== undefined) dbUpdates.required = updates.required;
        if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
        if (Object.keys(dbUpdates).length > 0) {
          supabase.from('custom_field_definitions').update(dbUpdates).eq('id', id).then();
        }
      },

      deleteCustomFieldDefinition: (id) => {
        set((state) => ({
          customFieldDefinitions: state.customFieldDefinitions.filter(f => f.id !== id),
          teamCustomFieldValues: state.teamCustomFieldValues.filter(v => v.fieldId !== id),
        }));
        supabase.from('custom_field_definitions').delete().eq('id', id).then();
      },

      reorderCustomFieldDefinitions: (orderedIds) => {
        set((state) => {
          const reordered = orderedIds
            .map((id, idx) => {
              const field = state.customFieldDefinitions.find(f => f.id === id);
              return field ? { ...field, sortOrder: idx } : null;
            })
            .filter(Boolean) as CustomFieldDefinition[];
          return { customFieldDefinitions: reordered };
        });
        orderedIds.forEach((id, idx) => {
          supabase.from('custom_field_definitions').update({ sort_order: idx }).eq('id', id).then();
        });
      },

      // ── Team Custom Field Values ───────────────────────────

      setTeamCustomFieldValue: (teamId, fieldId, value) => {
        const state = useStore.getState();
        const existing = state.teamCustomFieldValues.find(
          v => v.teamId === teamId && v.fieldId === fieldId
        );
        if (existing) {
          set((s) => ({
            teamCustomFieldValues: s.teamCustomFieldValues.map(
              v => v.id === existing.id ? { ...v, value, updatedAt: new Date().toISOString() } : v
            )
          }));
          supabase.from('staff_custom_field_values')
            .update({ value })
            .eq('id', existing.id)
            .then(({ error }) => {
              if (error) console.error('[setTeamCustomFieldValue] Update error:', error.message);
            });
        } else {
          const newId = uuidv4();
          const now = new Date().toISOString();
          set((s) => ({
            teamCustomFieldValues: [
              ...s.teamCustomFieldValues,
              { id: newId, fieldId, teamId, value, createdAt: now, updatedAt: now }
            ]
          }));
          supabase.from('staff_custom_field_values').insert({
            id: newId, field_id: fieldId, staff_id: teamId, value
          }).then(({ error }) => {
            if (error) console.error('[setTeamCustomFieldValue] Insert error:', error.message);
          });
        }
      },

      setTeamCustomFieldValues: (teamId, values) => {
        values.forEach(({ fieldId, value }) => {
          const state = useStore.getState();
          const existing = state.teamCustomFieldValues.find(
            v => v.teamId === teamId && v.fieldId === fieldId
          );
          if (existing) {
            set((s) => ({
              teamCustomFieldValues: s.teamCustomFieldValues.map(
                v => v.id === existing.id ? { ...v, value, updatedAt: new Date().toISOString() } : v
              )
            }));
            supabase.from('staff_custom_field_values')
              .update({ value }).eq('id', existing.id).then();
          } else {
            const newId = uuidv4();
            const now = new Date().toISOString();
            set((s) => ({
              teamCustomFieldValues: [
                ...s.teamCustomFieldValues,
                { id: newId, fieldId, teamId, value, createdAt: now, updatedAt: now }
              ]
            }));
            supabase.from('staff_custom_field_values').insert({
              id: newId, field_id: fieldId, staff_id: teamId, value
            }).then();
          }
        });
      },
    })
);
